import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';

export interface UserNotification {
  id: number;
  userId: number;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationMeta {
  correlation_id: string;
  page?: number;
  limit?: number;
  total?: number;
  unread_total?: number;
  total_pages?: number;
  has_next?: boolean;
  has_prev?: boolean;
}

interface ApiResponse<T> {
  data: T;
  error: null;
  meta: NotificationMeta;
}

@Injectable({
  providedIn: 'root',
})
export class UserNotificationsService {
  private api = inject(ApiService);
  private readonly baseUrl = 'v1/notifications';

  getNotifications(params?: {
    page?: number;
    limit?: number;
  }): Observable<{ data: UserNotification[]; meta: NotificationMeta }> {
    return this.api.get<ApiResponse<UserNotification[]>>(this.baseUrl, { params }).pipe(
      map((res) => {
        const raw = res as ApiResponse<UserNotification[] | unknown>;
        const extracted = this.extractNotificationList(raw.data);
        const normalized = extracted.map((n) => this.normalizeNotification(n));
        return {
          data: normalized,
          meta: raw.meta ?? { correlation_id: '' },
        };
      }),
    );
  }

  /** Backend flattens paginated payloads to `data: T[]`; tolerate accidental nesting. */
  private extractNotificationList(payload: unknown): UserNotification[] {
    if (Array.isArray(payload)) {
      return payload as UserNotification[];
    }
    if (payload && typeof payload === 'object' && 'data' in payload) {
      const inner = (payload as { data: unknown }).data;
      if (Array.isArray(inner)) {
        return inner as UserNotification[];
      }
    }
    return [];
  }

  /** Map snake_case API fields if present. */
  private normalizeNotification(row: unknown): UserNotification {
    const r = row as Record<string, unknown>;
    const isRead =
      typeof r['isRead'] === 'boolean'
        ? r['isRead']
        : typeof r['is_read'] === 'boolean'
          ? r['is_read']
          : false;
    const createdAt =
      typeof r['createdAt'] === 'string'
        ? r['createdAt']
        : typeof r['created_at'] === 'string'
          ? r['created_at']
          : '';
    const updatedAt =
      typeof r['updatedAt'] === 'string'
        ? r['updatedAt']
        : typeof r['updated_at'] === 'string'
          ? r['updated_at']
          : '';
    return {
      id: Number(r['id']),
      userId: Number(r['userId'] ?? r['user_id']),
      title: String(r['title'] ?? ''),
      message: String(r['message'] ?? ''),
      isRead,
      createdAt,
      updatedAt,
    };
  }

  markAsRead(id: number): Observable<void> {
    return this.api.post<void>(`${this.baseUrl}/${id}/read`, {});
  }

  markAllAsRead(): Observable<void> {
    return this.api.post<void>(`${this.baseUrl}/read-all`, {});
  }

  getUnreadCount(): Observable<number> {
    return this.api
      .get<{ data: { unreadCount: number } }>(`${this.baseUrl}/unread-count`)
      .pipe(map((res) => res.data.unreadCount));
  }
}
